import React, { useState, useRef, useEffect } from 'react';
import { Camera, ArrowLeft, ChevronUp, ChevronDown, User, LogOut, Settings, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import LanguageSelector from '../FullChat/LanguageSelector';
import '../../css/components/Profile/ProfileHeader.css';

const ProfileHeader = ({ user, isMobile, currentThemeName }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { logout } = useAuth();
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };



  const handleUserClick = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const handleChatPageClick = () => {
    setShowUserDropdown(false);
    navigate('/fullchat');
  };

  const handleLanguageClick = (languageCode) => {
    changeLanguage(languageCode);
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      setShowUserDropdown(false);
      
      // Call the logout function from auth context
      await logout();
      
      // Navigate to login page after successful logout
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // You can add error handling here (e.g., show a notification)
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Get theme colors based on current theme
  const getThemeColors = () => {
    const themeName = currentThemeName?.toLowerCase() || 'white';
    
    switch (themeName) {
      case 'dark':
        return {
          background: '#1a1a1a',
          cardBg: '#2d2d2d',
          cardHoverBg: '#3a3a3a',
          primaryBorder: '#404040',
          primaryText: '#ffffff',
          secondaryText: '#b3b3b3',
          primary: '#3b82f6',
          modalBg: '#1a1a1a'
        };
      case 'beige':
        return {
          background: '#f5f5dc',
          cardBg: '#fafaf0',
          cardHoverBg: '#f0f0e0',
          primaryBorder: '#d4d4b0',
          primaryText: '#4a4a2a',
          secondaryText: '#6b6b4a',
          primary: '#8b7355',
          modalBg: '#f5f5dc'
        };
      case 'white':
      default:
        return {
          background: '#ffffff',
          cardBg: '#f8fafc',
          cardHoverBg: '#f1f5f9',
          primaryBorder: '#e2e8f0',
          primaryText: '#1e293b',
          secondaryText: '#64748b',
          primary: '#3b82f6',
          modalBg: '#ffffff'
        };
    }
  };

  const themeColors = getThemeColors();

  return (
    <header 
      className={`profile-page-header ${currentThemeName ? `${currentThemeName}-theme` : ''}`}
      data-theme={currentThemeName || 'dark'}
      style={{ 
        background: theme.modalBg || theme.cardBg || theme.background || theme.primaryBg || '#ffffff', 
        borderBottom: `1px solid ${theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || 'rgba(0, 0, 0, 0.1)'}`,
        backdropFilter: 'blur(20px)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        overflow: 'visible'
      }}
    >
      
      {/* Left Section - Back Button */}
      <div className="header-left" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <button
          onClick={handleBackClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            background: theme.cardBg || theme.background || theme.secondaryBg || '#f8fafc',
            border: `1px solid ${theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0'}`,
            borderRadius: '8px',
            color: theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = theme.cardHoverBg || theme.background || theme.secondaryBg || '#f1f5f9';
            e.currentTarget.style.color = theme.primaryText || theme.text || theme.primaryText || '#1e293b';
            e.currentTarget.style.borderColor = theme.primary || theme.primaryColor || theme.accent || '#3b82f6';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = theme.cardBg || theme.background || theme.secondaryBg || '#f8fafc';
            e.currentTarget.style.color = theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b';
            e.currentTarget.style.borderColor = theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          }}
        >
          <ArrowLeft size={16} />
          {t('common.back')}
        </button>

        {/* Oakland AI Logo */}
        <div
          onClick={() => navigate('/fullchat')}
          style={{
            fontSize: '24px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.02em',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '8px'
          }}
          title="Go to main page"
        >
          Oakland AI
        </div>
      </div>



      {/* Right Section - Language and User Controls */}
      <div className="header-right" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: isMobile ? 'auto' : '200px',
        justifyContent: 'flex-end',
        overflow: 'visible'
      }}>
        
        {/* Language Button */}
        <button
          onClick={() => setShowLanguageSelector(true)}
          className="language-button"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            background: theme.cardBg || theme.background || theme.secondaryBg || '#f8fafc',
            borderRadius: '8px',
            border: `1px solid ${theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0'}`,
            fontSize: '13px',
            fontWeight: '500',
            color: theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = theme.cardHoverBg || theme.background || theme.secondaryBg || '#f1f5f9';
            e.currentTarget.style.borderColor = theme.primary || theme.primaryColor || theme.accent || '#3b82f6';
            e.currentTarget.style.color = theme.primaryText || theme.text || theme.primaryText || '#1e293b';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = theme.cardBg || theme.background || theme.secondaryBg || '#f8fafc';
            e.currentTarget.style.borderColor = theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0';
            e.currentTarget.style.color = theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          }}
        >
          <Globe size={14} />
          <span>{currentLanguage.toUpperCase()}</span>
          <ChevronDown size={12} />
        </button>

        {/* User Profile with Dropdown - Simplified on mobile */}
        <div className="user-profile-container" ref={dropdownRef} style={{ position: 'relative' }}>
          <div 
            className="user-profile" 
            onClick={handleUserClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '6px' : '8px',
              padding: isMobile ? '4px 8px' : '6px 12px',
              background: showUserDropdown ? (theme.cardHoverBg || theme.background || theme.secondaryBg || '#f1f5f9') : (theme.cardBg || theme.background || theme.secondaryBg || '#f8fafc'),
              borderRadius: '8px',
              border: `1px solid ${showUserDropdown ? (theme.primary || theme.primaryColor || theme.accent || '#3b82f6') : (theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0')}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{
              width: isMobile ? '24px' : '28px',
              height: isMobile ? '24px' : '28px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: isMobile ? '10px' : '12px',
              fontWeight: '600',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
              {user?.first_name?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            {!isMobile && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1px'
              }}>
                <span style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: theme.primaryText || theme.text || theme.primaryText || '#1e293b',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                }}>
                  {user?.first_name || user?.name || 'User'}
                </span>
                <span style={{
                  fontSize: '11px',
                  color: theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                }}>
                  {user?.business_type || 'Business Owner'}
                </span>
              </div>
            )}
            {showUserDropdown ? (
              <ChevronUp size={isMobile ? 10 : 12} style={{ color: theme.primary || theme.primaryColor || theme.accent || '#3b82f6' }} />
            ) : (
              <ChevronDown size={isMobile ? 10 : 12} style={{ color: theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b' }} />
            )}
          </div>

          {/* User Dropdown Menu - Simplified on mobile */}
          {showUserDropdown && (
            <div className="user-dropdown" style={{
              position: 'absolute',
              top: '100%',
              right: isMobile ? '0' : '0',
              marginTop: '8px',
              background: theme.modalBg || theme.cardBg || theme.background || theme.primaryBg || '#ffffff',
              border: `1px solid ${theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0'}`,
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(20px)',
              minWidth: isMobile ? '160px' : '200px',
              zIndex: 9999,
              animation: 'slideInDown 0.2s ease',
              overflow: 'hidden',
              visibility: 'visible',
              opacity: 1
            }}>
              {/* User Info Section - Simplified on mobile */}
              {!isMobile && (
                <div style={{
                  padding: '16px',
                  borderBottom: `1px solid ${theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0'}`,
                  background: theme.cardBg || theme.background || theme.secondaryBg || '#f8fafc'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: '600',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                    }}>
                      {user?.first_name?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: theme.primaryText || theme.text || theme.primaryText || '#1e293b',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                      }}>
                        {user?.first_name || user?.name || 'User'}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                      }}>
                        {user?.email || 'user@example.com'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Menu Items */}
              <div style={{ padding: isMobile ? '6px' : '8px' }}>
                {/* Chat Page */}
                <button
                  onClick={handleChatPageClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '8px' : '12px',
                    width: '100%',
                    padding: isMobile ? '10px 12px' : '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: theme.primaryText || theme.text || theme.primaryText || '#1e293b',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontSize: isMobile ? '13px' : '14px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = theme.cardHoverBg || theme.background || theme.secondaryBg || '#f1f5f9';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <User size={isMobile ? 14 : 16} />
                  <span>{t('common.fullChat')}</span>
                </button>

                {/* Divider */}
                <div style={{
                  height: '1px',
                  background: theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0',
                  margin: isMobile ? '6px 12px' : '8px 16px'
                }} />

                {/* Log Out */}
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '8px' : '12px',
                    width: '100%',
                    padding: isMobile ? '10px 12px' : '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: isLoggingOut ? (theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b') : '#ef4444',
                    cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontSize: isMobile ? '13px' : '14px',
                    textAlign: 'left',
                    opacity: isLoggingOut ? 0.6 : 1
                  }}
                  onMouseEnter={e => {
                    if (!isLoggingOut) {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isLoggingOut) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <LogOut size={isMobile ? 14 : 16} />
                  <span>{isLoggingOut ? t('common.loggingOut') : t('common.logout')}</span>
                  {isLoggingOut && (
                    <div style={{
                      marginLeft: 'auto',
                      width: '12px',
                      height: '12px',
                      border: '2px solid transparent',
                      borderTop: `2px solid ${theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b'}`,
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Language Selector Modal */}
      <LanguageSelector
        isOpen={showLanguageSelector}
        onClose={() => setShowLanguageSelector(false)}
        onLanguageClick={handleLanguageClick}
      />
    </header>
  );
};

export default ProfileHeader; 